import { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { $createRangeSelection, $getSelection, $isRangeSelection, $nodesOfType, $setSelection } from 'lexical';
import { SectionAnchorNode } from '../nodes/SectionAnchorNode';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { $createImageNode } from '../nodes/ImageNode';
import { $createPollNode } from '../nodes/PollNode';
import { INSERT_EQUATION_COMMAND } from '../plugins/EquationsPlugin';
import { INSERT_LAYOUT_COMMAND } from '../plugins/ColumnsLayoutPlugin';
import { INSERT_SECTION_ANCHOR_COMMAND } from '../plugins/SectionAnchorPlugin';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';

export function InsertImageDialog({ editor, onClose, onUploadImage }) {
  const [mode, setMode] = useState(null);
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);

  const onConfirmUrl = () => {
    if (src.trim()) {
      editor.update(() => {
        const imageNode = $createImageNode({ altText: altText || 'Image', src, maxWidth: 800 });
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([imageNode]);
        }
      });
      onClose();
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (onUploadImage) {
      setUploading(true);
      try {
        const url = await onUploadImage(file);
        if (url) {
          editor.update(() => {
            const imageNode = $createImageNode({
              altText: altText || file.name || 'Image',
              src: url,
              maxWidth: 800,
            });
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertNodes([imageNode]);
            }
          });
          onClose();
        }
      } finally {
        setUploading(false);
      }
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        editor.update(() => {
          const imageNode = $createImageNode({
            altText: altText || file.name || 'Image',
            src: dataUrl,
            maxWidth: 800,
          });
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes([imageNode]);
          }
        });
        onClose();
      };
      reader.readAsDataURL(file);
    }
  };

  if (mode === null) {
    return (
      <Stack spacing={1.5}>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => setMode('url')}
          sx={{
            justifyContent: 'flex-start',
            borderColor: '#d1d5db',
            color: '#232536',
            textTransform: 'none',
            fontWeight: 500,
            py: 1.5,
            '&:hover': { borderColor: '#232536', backgroundColor: 'rgba(35,37,54,0.04)' },
          }}
        >
          URL
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => setMode('file')}
          sx={{
            justifyContent: 'flex-start',
            borderColor: '#d1d5db',
            color: '#232536',
            textTransform: 'none',
            fontWeight: 500,
            py: 1.5,
            '&:hover': { borderColor: '#232536', backgroundColor: 'rgba(35,37,54,0.04)' },
          }}
        >
          File Upload
        </Button>
      </Stack>
    );
  }

  if (mode === 'file') {
    return (
      <Stack spacing={2}>
        <TextField
          label="Alt Text"
          placeholder="Descriptive text"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          size="small"
          fullWidth
          disabled={uploading}
          sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
        />
        <Button
          variant="outlined"
          component="label"
          fullWidth
          disabled={uploading}
          sx={{
            borderColor: '#ffcf51',
            color: '#232536',
            backgroundColor: 'rgba(255,207,81,0.08)',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': { borderColor: '#232536', backgroundColor: 'rgba(255,207,81,0.18)' },
            '&.Mui-disabled': { borderColor: '#ccc', backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
        >
          {uploading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: '#232536' }} />
              Uploading...
            </>
          ) : (
            'Choose Image File'
          )}
          <input type="file" accept="image/*" hidden onChange={onFileChange} disabled={uploading} />
        </Button>
        {uploading && (
          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Uploading image, please wait...
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <TextField
        label="Image URL"
        placeholder="https://example.com/image.jpg"
        value={src}
        onChange={(e) => setSrc(e.target.value)}
        size="small"
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <TextField
        label="Alt Text"
        placeholder="Descriptive text"
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        size="small"
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <Button
        variant="contained"
        disabled={!src.trim()}
        onClick={onConfirmUrl}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          backgroundColor: '#232536',
          '&:hover': { backgroundColor: '#3a3e5a' },
          '&.Mui-disabled': { backgroundColor: 'rgba(35,37,54,0.2)', color: 'rgba(255,255,255,0.5)' },
        }}
      >
        Confirm
      </Button>
    </Stack>
  );
}

export function InsertGIFDialog({ editor, onClose }) {
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');

  const onConfirm = () => {
    if (src.trim()) {
      editor.update(() => {
        const imageNode = $createImageNode({
          altText: altText || 'GIF',
          src: src.trim(),
          maxWidth: 500,
        });
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([imageNode]);
        }
      });
      onClose();
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="GIF URL"
        placeholder="https://media.giphy.com/..."
        value={src}
        onChange={(e) => setSrc(e.target.value)}
        size="small"
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <TextField
        label="Alt Text"
        placeholder="Descriptive text (optional)"
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        size="small"
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <Button
        variant="contained"
        disabled={!src.trim()}
        onClick={onConfirm}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          backgroundColor: '#232536',
          '&:hover': { backgroundColor: '#3a3e5a' },
          '&.Mui-disabled': { backgroundColor: 'rgba(35,37,54,0.2)', color: 'rgba(255,255,255,0.5)' },
        }}
      >
        Confirm
      </Button>
    </Stack>
  );
}

export function InsertPollDialog({ editor, onClose }) {
  const [question, setQuestion] = useState('');

  const onClick = () => {
    if (question.trim()) {
      editor.update(() => {
        const pollNode = $createPollNode(question);
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([pollNode]);
        }
      });
      onClose();
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Question"
        placeholder="What is your favorite..."
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        size="small"
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <Button
        variant="contained"
        disabled={!question.trim()}
        onClick={onClick}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          backgroundColor: '#232536',
          '&:hover': { backgroundColor: '#3a3e5a' },
          '&.Mui-disabled': { backgroundColor: 'rgba(35,37,54,0.2)', color: 'rgba(255,255,255,0.5)' },
        }}
      >
        Confirm
      </Button>
    </Stack>
  );
}

export function InsertTableDialog({ editor, onClose }) {
  const [rows, setRows] = useState('5');
  const [columns, setColumns] = useState('5');
  const [includeHeaders, setIncludeHeaders] = useState(true);

  const onClick = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns, rows, includeHeaders });
    onClose();
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Rows"
        type="number"
        placeholder="# of rows (1-500)"
        value={rows}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '' || parseInt(val, 10) >= 1) setRows(val);
        }}
        size="small"
        fullWidth
        slotProps={{ htmlInput: { min: 1, max: 500 } }}
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <TextField
        label="Columns"
        type="number"
        placeholder="# of columns (1-50)"
        value={columns}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '' || parseInt(val, 10) >= 1) setColumns(val);
        }}
        size="small"
        fullWidth
        slotProps={{ htmlInput: { min: 1, max: 50 } }}
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={includeHeaders}
            onChange={(e) => setIncludeHeaders(e.target.checked)}
            size="small"
            sx={{ color: '#232536', '&.Mui-checked': { color: '#232536' } }}
          />
        }
        label="Include headers row"
      />
      <Button
        variant="contained"
        onClick={onClick}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          backgroundColor: '#232536',
          '&:hover': { backgroundColor: '#3a3e5a' },
        }}
      >
        Confirm
      </Button>
    </Stack>
  );
}

export function InsertEquationDialog({ editor, onClose }) {
  const [equation, setEquation] = useState('');
  const [inline, setInline] = useState(true);

  const onClick = () => {
    editor.dispatchCommand(INSERT_EQUATION_COMMAND, { equation, inline });
    onClose();
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Equation (LaTeX)"
        placeholder="E = mc^2"
        value={equation}
        onChange={(e) => setEquation(e.target.value)}
        size="small"
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderColor: '#232536',
          },
          '& .MuiInputBase-input:focus': {
            color: '#232536',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#232536',
          },
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={inline}
            onChange={(e) => setInline(e.target.checked)}
            size="small"
            sx={{ color: '#232536', '&.Mui-checked': { color: '#232536' } }}
          />
        }
        label="Inline"
      />
      <Button
        variant="contained"
        disabled={!equation.trim()}
        onClick={onClick}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          backgroundColor: '#232536',
          '&:hover': { backgroundColor: '#3a3e5a' },
          '&.Mui-disabled': { backgroundColor: 'rgba(35,37,54,0.2)', color: 'rgba(255,255,255,0.5)' },
        }}
      >
        Confirm
      </Button>
    </Stack>
  );
}

function ColumnPreviewIcon({ value, selected }) {
  const cols = value.trim().split(/\s+/);
  const total = cols.reduce((sum, v) => sum + parseFloat(v), 0);
  return (
    <Box sx={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
      {cols.map((fr, i) => (
        <Box
          key={i}
          sx={{
            height: 18,
            width: `${(parseFloat(fr) / total) * 48}px`,
            borderRadius: '2px',
            backgroundColor: selected ? '#374151' : '#d1d5db',
            transition: 'background-color 0.15s',
          }}
        />
      ))}
    </Box>
  );
}

const COLUMN_LAYOUTS = [
  { label: '2 columns (equal width)', value: '1fr 1fr' },
  { label: '2 columns (25% - 75%)', value: '1fr 3fr' },
  { label: '3 columns (equal width)', value: '1fr 1fr 1fr' },
  { label: '3 columns (25% - 50% - 25%)', value: '1fr 2fr 1fr' },
  { label: '4 columns (equal width)', value: '1fr 1fr 1fr 1fr' },
];

export function InsertColumnsDialog({ editor, onClose }) {
  const [selected, setSelected] = useState(COLUMN_LAYOUTS[0].value);
  const [customColumns, setCustomColumns] = useState('');

  const insert = (template) => {
    editor.dispatchCommand(INSERT_LAYOUT_COMMAND, template);
    onClose();
  };

  const onConfirmCustom = () => {
    const num = parseInt(customColumns, 10);
    if (num >= 2 && num <= 10) {
      insert(Array(num).fill('1fr').join(' '));
    }
  };

  return (
    <Stack spacing={1.5} sx={{ minWidth: 260 }}>
      <Stack spacing={0.75}>
        {COLUMN_LAYOUTS.map(({ label, value }) => {
          const isSelected = selected === value;
          return (
            <Box
              key={value}
              onClick={() => setSelected(value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                border: '1.5px solid',
                borderColor: isSelected ? '#374151' : '#e5e7eb',
                backgroundColor: isSelected ? '#f9fafb' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' },
              }}
            >
              <ColumnPreviewIcon value={value} selected={isSelected} />
              <Typography sx={{ fontSize: 13, color: isSelected ? '#111827' : '#374151', fontWeight: isSelected ? 600 : 400 }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Button
        variant="contained"
        onClick={() => insert(selected)}
        disableElevation
        sx={{
          textTransform: 'none',
          backgroundColor: '#111827',
          color: '#fff',
          borderRadius: 1.5,
          fontWeight: 600,
          '&:hover': { backgroundColor: '#1f2937' },
        }}
      >
        Insert
      </Button>

      <Divider sx={{ borderColor: '#f3f4f6' }} />

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label="Custom columns"
          type="number"
          placeholder="e.g. 5"
          value={customColumns}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || parseInt(val, 10) >= 1) setCustomColumns(val);
          }}
          size="small"
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
              borderColor: '#232536',
            },
            '& .MuiInputBase-input:focus': {
              color: '#232536',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#232536',
            },
          }}
          slotProps={{ htmlInput: { min: 2, max: 10 } }}
        />
        <Button
          variant="outlined"
          onClick={onConfirmCustom}
          disabled={!customColumns}
          sx={{
            whiteSpace: 'nowrap',
            textTransform: 'none',
            borderColor: '#d1d5db',
            color: '#374151',
            borderRadius: 1.5,
            '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' },
          }}
        >
          Insert
        </Button>
      </Stack>
    </Stack>
  );
}

const sectionIdTextFieldSx = {
  '& .MuiOutlinedInput-root:focus-within .MuiOutlinedInput-notchedOutline': {
    borderColor: '#232536',
  },
  '& .MuiInputBase-input:focus': {
    color: '#232536',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#232536',
  },
};

const confirmButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#232536',
  '&:hover': { backgroundColor: '#3a3e5a' },
  '&.Mui-disabled': { backgroundColor: 'rgba(35,37,54,0.2)', color: 'rgba(255,255,255,0.5)' },
};

export function InsertSectionAnchorDialog({ editor, onClose, savedSel }) {
  const [sectionId, setSectionId] = useState('');
  const [error, setError] = useState('');
  const [existingIds, setExistingIds] = useState([]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const nodes = $nodesOfType(SectionAnchorNode);
      setExistingIds(nodes.map((n) => n.getSectionId()));
    });
  }, [editor]);

  const sanitizeSectionId = (value) => {
    return value.replace(/[^a-zA-Z0-9._-]/g, '');
  };

  const onClick = () => {
    if (!savedSel) {
      setError('Select some text first, then open this dialog.');
      return;
    }

    const trimmed = sectionId.trim();
    if (!trimmed) {
      setError('Section ID is required.');
      return;
    }
    if (!/^[a-zA-Z][a-zA-Z0-9._-]*$/.test(trimmed)) {
      setError('ID must start with a letter and contain only letters, numbers, dots, hyphens, or underscores.');
      return;
    }
    if (existingIds.includes(trimmed)) {
      setError('Reference name already exists. Please use a unique name.');
      return;
    }

    editor.dispatchCommand(INSERT_SECTION_ANCHOR_COMMAND, { sectionId: trimmed, savedSel });
    onClose();
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: '#6b7280' }}>
        Select the text to refer to and enter a clear, unique reference name
      </Typography>
      <TextField
        label="Text input"
        placeholder="e.g. introduction, section1"
        value={sectionId}
        onChange={(e) => {
          setSectionId(sanitizeSectionId(e.target.value));
          if (error) setError('');
        }}
        size="small"
        fullWidth
        
        error={!!error}
        helperText={error || 'Use letters, numbers, dots, hyphens, or underscores (e.g. section1.1). Must start with a letter.'}
        sx={sectionIdTextFieldSx}
      />
      <Button
        variant="contained"
        disabled={!sectionId.trim()}
        onClick={onClick}
        sx={confirmButtonSx}
      >
        Save
      </Button>
    </Stack>
  );
}

export function InsertInternalLinkDialog({ editor, onClose, savedSel }) {
  const [sectionId, setSectionId] = useState('');
  const [error, setError] = useState('');
  const [existingIds, setExistingIds] = useState([]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const nodes = $nodesOfType(SectionAnchorNode);
      setExistingIds(nodes.map((n) => n.getSectionId()));
    });
  }, [editor]);

  const onClick = () => {
    if (!savedSel) {
      setError('Select some text first, then open this dialog.');
      return;
    }

    const trimmed = sectionId.trim();
    if (!trimmed) {
      setError('Reference name is required.');
      return;
    }
    if (!existingIds.includes(trimmed)) {
      setError('No matching reference found. Please check the name.');
      return;
    }

    // TOGGLE_LINK_COMMAND reads $getSelection() internally.
    // Restore the snapshotted selection before dispatching.
    editor.update(() => {
      const newSel = $createRangeSelection();
      newSel.anchor.key = savedSel.anchorKey;
      newSel.anchor.offset = savedSel.anchorOffset;
      newSel.anchor.type = savedSel.anchorType;
      newSel.focus.key = savedSel.focusKey;
      newSel.focus.offset = savedSel.focusOffset;
      newSel.focus.type = savedSel.focusType;
      $setSelection(newSel);
    }, { onUpdate: () => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, `#${trimmed}`);
    }});

    onClose();
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: '#6b7280' }}>
        Select text in the editor first, then choose a reference from the dropdown. The selected text will become a link that scrolls to that section.
      </Typography>
      {existingIds.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#b45309', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, px: 1.5, py: 1 }}>
          No references found in this document. Use the &#34;Insert → Section Anchor&#34; option to mark a section first.
        </Typography>
      ) : (
        <Autocomplete
          freeSolo
          selectOnFocus
          handleHomeEndKeys
          options={existingIds}
          value={sectionId}
          inputValue={sectionId}
          onInputChange={(event, newValue) => {
            setSectionId(newValue ?? '');
            if (error) setError('');
          }}
          onChange={(event, newValue) => {
            setSectionId(newValue ?? '');
            if (error) setError('');
          }}
          noOptionsText="No matching reference found. Please check the name."
          renderInput={(params) => (
            <TextField
              {...params}
              label="Target Reference"
              placeholder="e.g. introduction, section1"
              size="small"
              error={!!error}
              helperText={error || (sectionId.trim() ? `Will link to #${sectionId.trim()}` : 'Type or select a reference name')}
              sx={sectionIdTextFieldSx}
            />
          )}
        />
      )}
      <Button
        variant="contained"
        disabled={!sectionId.trim() || existingIds.length === 0}
        onClick={onClick}
        sx={confirmButtonSx}
      >
        Insert Link
      </Button>
    </Stack>
  );
}
